import * as client from "../../../client";

/** A section row: a lone form, or a default/alternate pair shown together. */
export type SectionEntry =
  | { kind: "single"; form: client.FormSelection }
  | {
      kind: "pair";
      pair: client.FormPairView;
      /** Position in the response's pair list, for a stable per-pair color. */
      pairIndex: number;
      defaultForm: client.FormSelection;
      alternateForm: client.FormSelection;
    };

/**
 * The entries of one group, in catalog order. A pair sits where its default
 * form does, with the alternate pulled in from its own group beneath it.
 */
export const sectionEntries = (
  forms: client.FormSelection[],
  pairs: client.FormPairView[],
  group: client.FormGroup
): SectionEntry[] => {
  const byId = new Map(forms.map((form) => [form.form_id, form]));
  const pairByDefault = new Map(
    pairs.map((pair, pairIndex) => [pair.default_form_id, { pair, pairIndex }])
  );
  const alternateIds = new Set(pairs.map((pair) => pair.alternate_form_id));
  const entries: SectionEntry[] = [];
  for (const form of forms) {
    if (form.group !== group) continue;
    const paired = pairByDefault.get(form.form_id);
    const alternateForm = paired && byId.get(paired.pair.alternate_form_id);
    if (paired && alternateForm) {
      entries.push({
        kind: "pair",
        pair: paired.pair,
        pairIndex: paired.pairIndex,
        defaultForm: form,
        alternateForm,
      });
    } else if (!alternateIds.has(form.form_id)) {
      entries.push({ kind: "single", form });
    }
  }
  return entries;
};

export const entryForms = (entry: SectionEntry): client.FormSelection[] =>
  entry.kind === "single"
    ? [entry.form]
    : [entry.defaultForm, entry.alternateForm];

/** The other member of ``formId``'s pair, or null when it is unpaired. */
export const pairPartner = (
  pairs: client.FormPairView[],
  formId: string
): string | null => {
  for (const pair of pairs) {
    if (pair.default_form_id === formId) return pair.alternate_form_id;
    if (pair.alternate_form_id === formId) return pair.default_form_id;
  }
  return null;
};
